import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  Save, 
  X, 
  ShieldAlert, 
  Plus, 
  Loader2,
  Trash2,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getGameById, updateGame } from '../lib/gameService';
import type { Game, Store, StoreProduct } from '../types';
import { supabase } from '../lib/supabase';

// Categorias de jogos comuns
const GAME_CATEGORIES = [
  'Estratégia', 'Familiar', 'Party Game', 'Aventura', 'Cooperativo',
  'Deckbuilding', 'Euro', 'Temático', 'Tático', 'RPG',
  'Card Game', 'Educativo', 'Abstrato', 'Infantil', 'Competitivo'
];

// Mecânicas de jogos comuns
const GAME_MECHANICS = [
  'Deck Building', 'Worker Placement', 'Area Control', 'Dice Rolling',
  'Card Drafting', 'Hand Management', 'Tile Placement', 'Set Collection',
  'Role Playing', 'Cooperative Play', 'Action Points', 'Auction/Bidding'
];

// Temas de jogos comuns
const GAME_THEMES = [
  'Medieval', 'Fantasia', 'Sci-Fi', 'Horror', 'Histórico',
  'Guerra', 'Econômico', 'Exploração', 'Civilização', 'Zombies'
];

const EditGamePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingGame, setLoadingGame] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Corrigindo o tipo do formData
  const [formData, setFormData] = useState<Partial<Game>>({
    name: '',
    year: null,
    ano_nacional: null,
    description: null,
    image_url: null,
    categories: [],
    mechanics: [],
    themes: [],
    artists: [],
    designers: [],
    min_players: null,
    max_players: null,
    play_time: null,
    complexity: null,
    bgg_id: null
  });
  
  // Estado para categoria personalizada
  const [customCategory, setCustomCategory] = useState('');
  const [customMechanic, setCustomMechanic] = useState('');
  const [customTheme, setCustomTheme] = useState('');
  const [customArtist, setCustomArtist] = useState('');
  const [customDesigner, setCustomDesigner] = useState('');

  // Estados para gerenciamento de links de afiliados
  const [stores, setStores] = useState<Store[]>([]);
  const [storeProducts, setStoreProducts] = useState<StoreProduct[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [newStoreProduct, setNewStoreProduct] = useState<{
    store_id: string;
    product_url: string;
    price: string;
    available: boolean;
  }>({
    store_id: '',
    product_url: '',
    price: '',
    available: true
  });

  // Verificar se o usuário é administrador
  useEffect(() => {
    if (!isAdmin) {
      navigate('/search', { 
        state: { 
          message: 'Apenas administradores podem editar jogos na base de dados.' 
        } 
      });
    }
  }, [isAdmin, navigate]);

  // Carregar dados do jogo para edição
  useEffect(() => {
    const loadGameData = async () => {
      if (!id) return;
      
      try {
        setLoadingGame(true);
        const gameData = await getGameById(id);
        setFormData(gameData);
        
        // Carregar links de afiliados existentes
        await loadStoreProducts(id);
      } catch (err: any) {
        console.error('Erro ao carregar jogo:', err);
        setError('Não foi possível carregar os dados do jogo.');
      } finally {
        setLoadingGame(false);
      }
    };
    
    loadGameData();
  }, [id]);
  
  // Carregar lojas disponíveis
  useEffect(() => {
    const loadStores = async () => {
      try {
        setLoadingStores(true);
        const { data, error } = await supabase
          .from('stores')
          .select('*')
          .order('name');
          
        if (error) throw error;
        
        setStores(data || []);
        
        // Definir Amazon como loja padrão se existir
        const amazonStore = data?.find(store => 
          store.name.toLowerCase().includes('amazon')
        );
        
        if (amazonStore) {
          setNewStoreProduct(prev => ({
            ...prev,
            store_id: amazonStore.id
          }));
        }
      } catch (err) {
        console.error('Erro ao carregar lojas:', err);
      } finally {
        setLoadingStores(false);
      }
    };
    
    loadStores();
  }, []);
  
  // Função para carregar links de afiliados existentes
  const loadStoreProducts = async (gameId: string) => {
    try {
      const { data, error } = await supabase
        .from('store_products')
        .select('*, store:stores(*)')
        .eq('game_id', gameId);
        
      if (error) throw error;
      
      setStoreProducts(data || []);
    } catch (err) {
      console.error('Erro ao carregar links de afiliados:', err);
    }
  };
  
  // Função para adicionar novo link de afiliado
  const handleAddStoreProduct = async () => {
    if (!id || !newStoreProduct.store_id || !newStoreProduct.product_url) {
      setError('Selecione uma loja e informe a URL do produto');
      return;
    }
    
    try {
      setLoading(true);
      
      const newProduct = {
        game_id: id,
        store_id: newStoreProduct.store_id,
        product_url: newStoreProduct.product_url,
        price: newStoreProduct.price ? parseFloat(newStoreProduct.price) : null,
        available: newStoreProduct.available
      };
      
      const { data, error } = await supabase
        .from('store_products')
        .insert(newProduct)
        .select('*, store:stores(*)');
        
      if (error) throw error;
      
      // Adicionar o novo produto à lista
      if (data && data.length > 0) {
        setStoreProducts(prev => [...prev, data[0]]);
        
        // Limpar formulário
        setNewStoreProduct({
          store_id: '',
          product_url: '',
          price: '',
          available: true
        });
        
        setSuccessMessage('Link de afiliado adicionado com sucesso!');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      console.error('Erro ao adicionar link de afiliado:', err);
      setError(err.message || 'Erro ao adicionar link de afiliado');
    } finally {
      setLoading(false);
    }
  };
  
  // Função para remover um link de afiliado
  const handleRemoveStoreProduct = async (storeProductId: string) => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('store_products')
        .delete()
        .eq('id', storeProductId);
        
      if (error) throw error;
      
      // Remover o produto da lista
      setStoreProducts(prev => prev.filter(product => product.id !== storeProductId));
      
      setSuccessMessage('Link de afiliado removido com sucesso!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Erro ao remover link de afiliado:', err);
      setError(err.message || 'Erro ao remover link de afiliado');
    } finally {
      setLoading(false);
    }
  };

  // Verificação adicional antes de renderizar o conteúdo
  if (!isAdmin) {
    return (
      <div className="container mx-auto max-w-4xl py-12">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <ShieldAlert className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Acesso Restrito</h2>
          <p className="text-gray-600 mb-6">
            Apenas administradores podem editar jogos no catálogo.
            Você será redirecionado para a página de busca.
          </p>
          <Link
            to="/search"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition inline-flex items-center"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Ir para Busca
          </Link>
        </div>
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'number') {
      setFormData({
        ...formData,
        [name]: value ? parseFloat(value) : null
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleCategoryToggle = (category: string) => {
    const currentCategories = formData.categories || [];
    
    if (currentCategories.includes(category)) {
      setFormData({
        ...formData,
        categories: currentCategories.filter(c => c !== category)
      });
    } else {
      setFormData({
        ...formData,
        categories: [...currentCategories, category]
      });
    }
  };

  const handleMechanicToggle = (mechanic: string) => {
    const currentMechanics = formData.mechanics || [];
    
    if (currentMechanics.includes(mechanic)) {
      setFormData({
        ...formData,
        mechanics: currentMechanics.filter(m => m !== mechanic)
      });
    } else {
      setFormData({
        ...formData,
        mechanics: [...currentMechanics, mechanic]
      });
    }
  };

  const handleThemeToggle = (theme: string) => {
    const currentThemes = formData.themes || [];
    
    if (currentThemes.includes(theme)) {
      setFormData({
        ...formData,
        themes: currentThemes.filter(t => t !== theme)
      });
    } else {
      setFormData({
        ...formData,
        themes: [...currentThemes, theme]
      });
    }
  };

  const handleAddCustomCategory = () => {
    if (customCategory.trim()) {
      const currentCategories = formData.categories || [];
      if (!currentCategories.includes(customCategory)) {
        setFormData({
          ...formData,
          categories: [...currentCategories, customCategory.trim()]
        });
      }
      setCustomCategory('');
    }
  };

  const handleAddCustomMechanic = () => {
    if (customMechanic.trim()) {
      const currentMechanics = formData.mechanics || [];
      if (!currentMechanics.includes(customMechanic)) {
        setFormData({
          ...formData,
          mechanics: [...currentMechanics, customMechanic.trim()]
        });
      }
      setCustomMechanic('');
    }
  };

  const handleAddCustomTheme = () => {
    if (customTheme.trim()) {
      const currentThemes = formData.themes || [];
      if (!currentThemes.includes(customTheme)) {
        setFormData({
          ...formData,
          themes: [...currentThemes, customTheme.trim()]
        });
      }
      setCustomTheme('');
    }
  };

  const handleAddCustomArtist = () => {
    if (customArtist.trim()) {
      const currentArtists = formData.artists || [];
      if (!currentArtists.includes(customArtist)) {
        setFormData({
          ...formData,
          artists: [...currentArtists, customArtist.trim()]
        });
      }
      setCustomArtist('');
    }
  };

  const handleAddCustomDesigner = () => {
    if (customDesigner.trim()) {
      const currentDesigners = formData.designers || [];
      if (!currentDesigners.includes(customDesigner)) {
        setFormData({
          ...formData,
          designers: [...currentDesigners, customDesigner.trim()]
        });
      }
      setCustomDesigner('');
    }
  };

  const validateForm = (): boolean => {
    // Validação básica
    if (!formData.name) {
      setError('O nome do jogo é obrigatório');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Garantir que os arrays estejam inicializados corretamente e explicitamente definidos
      const gameDataToUpdate = {
        ...formData,
        artists: Array.isArray(formData.artists) ? formData.artists : [],
        designers: Array.isArray(formData.designers) ? formData.designers : [],
        categories: Array.isArray(formData.categories) ? formData.categories : [],
        mechanics: Array.isArray(formData.mechanics) ? formData.mechanics : [],
        themes: Array.isArray(formData.themes) ? formData.themes : []
      };
      
      console.log('Enviando dados para atualização:', JSON.stringify(gameDataToUpdate));
      
      // Atualizar jogo existente
      const updatedGame = await updateGame(id, gameDataToUpdate);
      
      setSuccessMessage('Jogo atualizado com sucesso!');
      
      // Redirecionar após um breve delay
      setTimeout(() => {
        navigate(`/games/titulo/${updatedGame.name.toLowerCase().replace(/\s+/g, '-')}`);
      }, 1500);
    } catch (err: any) {
      console.error('Erro ao atualizar jogo:', err);
      setError(err.message || 'Ocorreu um erro ao atualizar o jogo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingGame) {
    return (
      <div className="container mx-auto max-w-4xl py-12">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500"></div>
          <p className="ml-4 text-gray-600">Carregando dados do jogo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-5xl py-8">
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Editar Jogo</h1>
            <Link to={`/games/titulo/${formData.name?.toLowerCase().replace(/\s+/g, '-')}`} className="text-gray-500 hover:text-indigo-600">
              <X className="h-6 w-6" />
            </Link>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* Informações Básicas */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Informações Básicas</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Jogo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ano de Publicação
                    </label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year || ''}
                      onChange={handleChange}
                      min="1900"
                      max={new Date().getFullYear()}
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ano de Lançamento no Brasil
                    </label>
                    <input
                      type="number"
                      name="ano_nacional"
                      value={formData.ano_nacional || ''}
                      onChange={handleChange}
                      min="1900"
                      max={new Date().getFullYear()}
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL da Imagem
                    </label>
                    <input
                      type="url"
                      name="image_url"
                      value={formData.image_url || ''}
                      onChange={handleChange}
                      placeholder="https://..."
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {formData.image_url && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pré-visualização
                      </label>
                      <div className="h-32 w-32 border rounded-md overflow-hidden">
                        <img 
                          src={formData.image_url} 
                          alt="Pré-visualização" 
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Categorias */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Categorias</h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {GAME_CATEGORIES.map(category => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => handleCategoryToggle(category)}
                      className={`px-3 py-1 text-sm rounded-full ${
                        formData.categories?.includes(category)
                          ? 'bg-indigo-100 text-indigo-800 border border-indigo-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    placeholder="Categoria personalizada"
                    className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomCategory}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                    disabled={!customCategory.trim()}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                
                {formData.categories && formData.categories.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    Categorias selecionadas: {formData.categories.join(', ')}
                  </div>
                )}
              </div>
              
              {/* Mecânicas */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Mecânicas</h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {GAME_MECHANICS.map(mechanic => (
                    <button
                      key={mechanic}
                      type="button"
                      onClick={() => handleMechanicToggle(mechanic)}
                      className={`px-3 py-1 text-sm rounded-full ${
                        formData.mechanics?.includes(mechanic)
                          ? 'bg-teal-100 text-teal-800 border border-teal-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {mechanic}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customMechanic}
                    onChange={(e) => setCustomMechanic(e.target.value)}
                    placeholder="Mecânica personalizada"
                    className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomMechanic}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                    disabled={!customMechanic.trim()}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                
                {formData.mechanics && formData.mechanics.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    Mecânicas selecionadas: {formData.mechanics.join(', ')}
                  </div>
                )}
              </div>
              
              {/* Temas */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Temas</h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {GAME_THEMES.map(theme => (
                    <button
                      key={theme}
                      type="button"
                      onClick={() => handleThemeToggle(theme)}
                      className={`px-3 py-1 text-sm rounded-full ${
                        formData.themes?.includes(theme)
                          ? 'bg-amber-100 text-amber-800 border border-amber-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {theme}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customTheme}
                    onChange={(e) => setCustomTheme(e.target.value)}
                    placeholder="Tema personalizado"
                    className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomTheme}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                    disabled={!customTheme.trim()}
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                
                {formData.themes && formData.themes.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    Temas selecionados: {formData.themes.join(', ')}
                  </div>
                )}
              </div>
              
              {/* Artistas e Designers */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Créditos</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Designers
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customDesigner}
                        onChange={(e) => setCustomDesigner(e.target.value)}
                        placeholder="Adicionar designer"
                        className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomDesigner}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                        disabled={!customDesigner.trim()}
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    {formData.designers && formData.designers.length > 0 && (
                      <div className="mt-2">
                        <ul className="list-disc pl-5 text-sm text-gray-600">
                          {formData.designers.map((designer, index) => (
                            <li key={index}>{designer}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Artistas
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customArtist}
                        onChange={(e) => setCustomArtist(e.target.value)}
                        placeholder="Adicionar artista"
                        className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomArtist}
                        className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                        disabled={!customArtist.trim()}
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                    {formData.artists && formData.artists.length > 0 && (
                      <div className="mt-2">
                        <ul className="list-disc pl-5 text-sm text-gray-600">
                          {formData.artists.map((artist, index) => (
                            <li key={index}>{artist}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Detalhes do Jogo */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Detalhes do Jogo</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número Mínimo de Jogadores
                    </label>
                    <input
                      type="number"
                      name="min_players"
                      value={formData.min_players || ''}
                      onChange={handleChange}
                      min="1"
                      max="99"
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número Máximo de Jogadores
                    </label>
                    <input
                      type="number"
                      name="max_players"
                      value={formData.max_players || ''}
                      onChange={handleChange}
                      min="1"
                      max="99"
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tempo de Jogo (minutos)
                    </label>
                    <input
                      type="number"
                      name="play_time"
                      value={formData.play_time || ''}
                      onChange={handleChange}
                      min="1"
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Complexidade (1-5)
                    </label>
                    <input
                      type="number"
                      name="complexity"
                      value={formData.complexity || ''}
                      onChange={handleChange}
                      min="1"
                      max="5"
                      step="0.1"
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID no BoardGameGeek (opcional)
                    </label>
                    <input
                      type="number"
                      name="bgg_id"
                      value={formData.bgg_id || ''}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
              
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  rows={5}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                ></textarea>
              </div>
              
              {/* Seção de Links de Afiliados - Adicionar após outras seções, mas antes do botão de submissão */}
              <div className="border-t pt-6 mt-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Links de Afiliados</h2>
                
                {/* Lista de links existentes */}
                {storeProducts.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-md font-medium text-gray-700 mb-2">Links Cadastrados</h3>
                    <div className="bg-gray-50 rounded-md p-4">
                      <ul className="divide-y divide-gray-200">
                        {storeProducts.map(product => (
                          <li key={product.id} className="py-3 flex justify-between items-center">
                            <div>
                              <p className="font-medium">{product.store?.name || 'Loja'}</p>
                              <div className="flex items-center text-sm text-gray-500">
                                <a 
                                  href={product.product_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center hover:text-indigo-600"
                                >
                                  <ExternalLink size={14} className="mr-1" />
                                  {product.product_url.substring(0, 40)}...
                                </a>
                              </div>
                              {product.price && (
                                <p className="text-sm">Preço: R$ {product.price.toFixed(2)}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveStoreProduct(product.id)}
                              className="text-red-500 hover:text-red-700 p-2"
                              title="Remover link"
                            >
                              <Trash2 size={18} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                
                {/* Formulário para adicionar novo link */}
                <div className="bg-white border border-gray-200 rounded-md p-4">
                  <h3 className="text-md font-medium text-gray-700 mb-3">Adicionar Novo Link</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Loja
                      </label>
                      <select
                        value={newStoreProduct.store_id}
                        onChange={(e) => setNewStoreProduct({...newStoreProduct, store_id: e.target.value})}
                        className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        disabled={loadingStores}
                      >
                        <option value="">Selecione uma loja</option>
                        {stores.map(store => (
                          <option key={store.id} value={store.id}>
                            {store.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preço (opcional)
                      </label>
                      <input
                        type="number"
                        value={newStoreProduct.price}
                        onChange={(e) => setNewStoreProduct({...newStoreProduct, price: e.target.value})}
                        placeholder="99.90"
                        step="0.01"
                        min="0"
                        className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL do Produto
                    </label>
                    <input
                      type="url"
                      value={newStoreProduct.product_url}
                      onChange={(e) => setNewStoreProduct({...newStoreProduct, product_url: e.target.value})}
                      placeholder="https://www.exemplo.com/produto"
                      className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id="available"
                      checked={newStoreProduct.available}
                      onChange={(e) => setNewStoreProduct({...newStoreProduct, available: e.target.checked})}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <label htmlFor="available" className="ml-2 text-sm text-gray-700">
                      Produto disponível
                    </label>
                  </div>
                  
                  <div>
                    <button
                      type="button"
                      onClick={handleAddStoreProduct}
                      disabled={loading || !newStoreProduct.store_id || !newStoreProduct.product_url}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition disabled:bg-gray-400 flex items-center"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={16} className="animate-spin mr-2" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Plus size={16} className="mr-2" />
                          Adicionar Link
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Botões de ação */}
              <div className="flex gap-3 justify-end">
                <Link
                  to={`/games/titulo/${formData.name?.toLowerCase().replace(/\s+/g, '-')}`}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition flex items-center"
                >
                  <X className="h-5 w-5 mr-1" />
                  Cancelar
                </Link>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition flex items-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-1 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="h-5 w-5 mr-1" />
                      Salvar Alterações
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditGamePage; 